/**
 * Ce que l'on sait du site qui nous embarque — et comment lui parler.
 *
 * Deux consommateurs, qui ne se connaissent pas :
 *   - components/EmbedBridge.js  ALIMENTE ce module (il reçoit les messages du
 *     parent et pose l'URL de la page hôte ici) ;
 *   - services/paiementLauncher.js le LIT, pour renvoyer le donateur sur la page
 *     du client après paiement et pour sortir de l'iframe au bon moment.
 *
 * Passer par un module plutôt que par une variable globale garde le contrat
 * explicite et vérifiable : un seul endroit valide ce qui arrive du parent.
 */

import logger from "./logger";

// URL courante du site hôte, telle qu'il nous l'a transmise. `null` tant qu'on
// n'a rien reçu : une page hôte qui n'a pas mis son code d'intégration à jour
// ne dira jamais rien, et tout doit continuer de fonctionner sans elle.
let hostUrl = null;
let hostOrigin = null;

/** Sommes-nous rendus dans l'iframe de quelqu'un d'autre ? */
export function isEmbedded() {
    try {
        return typeof window !== "undefined" && window.parent !== window.self;
    } catch (e) {
        // Accès refusé à window.parent : c'est qu'il y en a un, d'une autre origine.
        return true;
    }
}

/**
 * Enregistre l'URL de la page hôte. Appelé par le pont à chaque fois que le
 * parent l'annonce (au démarrage, puis à ses propres changements de page).
 *
 * On n'accepte qu'une URL http(s) analysable : c'est une valeur qui finira dans
 * une redirection après paiement, elle ne doit jamais pouvoir porter autre chose
 * qu'une adresse de page (`javascript:` en particulier).
 */
export function setHostUrl(value, senderOrigin = null) {
    if (typeof value !== "string" || !value) return false;

    try {
        const url = new URL(value);
        if (url.protocol !== "http:" && url.protocol !== "https:") return false;
        if (senderOrigin && url.origin !== senderOrigin) return false;
        hostUrl = url.href;
        hostOrigin = senderOrigin || url.origin;
        while (hostUrlWaiters.length) hostUrlWaiters.shift()();
        return true;
    } catch (e) {
        logger.debug({ value }, "embedHost: URL de la page hôte ignorée");
        return false;
    }
}

/** L'URL de la page hôte, ou null si le parent ne l'a pas annoncée. */
export function getHostUrl() {
    return hostUrl;
}

// Appelants en attente de l'adresse de la page hôte (cf. onHostUrlReady).
const hostUrlWaiters = [];

/**
 * Attend de connaître l'adresse de la page hôte, puis appelle `callback`.
 *
 * POURQUOI : le parent ne l'annonce qu'après un aller-retour de messages, alors
 * que certains départs de paiement ont lieu AU MONTAGE de la page — un lien
 * court de don ouvert directement dans l'iframe, par exemple. Sans cette
 * attente, l'adresse de retour est calculée avant que le parent ait parlé, et
 * le donateur revient sur notre site au lieu de celui du client.
 *
 * Appelle immédiatement hors iframe ou si l'adresse est déjà connue, et se
 * déclenche de toute façon au bout de `timeoutMs` : mieux vaut une adresse de
 * repli qu'un tunnel qui ne démarre jamais.
 */
export function onHostUrlReady(callback, timeoutMs = 700) {
    if (typeof callback !== "function") return;
    if (!isEmbedded() || hostUrl) {
        callback();
        return;
    }

    let done = false;
    const fire = () => {
        if (done) return;
        done = true;
        callback();
    };

    hostUrlWaiters.push(fire);
    setTimeout(fire, timeoutMs);
}

/**
 * Demande au site hôte de naviguer LUI-MÊME vers `url`.
 *
 * Message `exit` — à ne pas confondre avec `navigate`, qui signale au parent que
 * l'internaute a changé de page DANS l'iframe.
 *
 * C'est le chemin de sortie le plus sûr : une page qui se navigue elle-même
 * n'est soumise à aucune condition — ni geste utilisateur récent, ni jeton de
 * bac à sable. Sans effet si le client n'a pas mis son code d'intégration à
 * jour, d'où le repli systématique de `navigateOut`.
 */
export function askHostToNavigate(url) {
    if (!isEmbedded() || !url) return;
    try {
        window.parent.postMessage({ source: "kehila-embed", type: "exit", url }, hostOrigin || "*");
    } catch (e) {
        /* parent parti : on ignore */
    }
}

/**
 * Sort de l'iframe vers `url`, en pleine fenêtre.
 *
 * Trois tentatives, de la plus fiable à la moins bonne :
 *   1. demander au parent de naviguer (aucune condition) ;
 *   2. naviguer la fenêtre du haut nous-mêmes — autorisé tant que le geste de
 *      l'utilisateur est encore valide, donc À CONDITION d'appeler cette
 *      fonction directement depuis un gestionnaire de clic, sans avoir attendu
 *      le serveur entre-temps ;
 *   3. à défaut, charger dans la frame courante : le tunnel s'affichera dans
 *      l'iframe, ce qu'on cherche à éviter, mais un clic sans effet serait pire.
 *
 * Hors iframe, navigation normale.
 */
export function navigateOut(url) {
    if (!url || typeof window === "undefined") return;

    if (!isEmbedded()) {
        window.location.href = url;
        return;
    }

    askHostToNavigate(url);

    try {
        window.top.location.href = url;
    } catch (e) {
        // Refus franc (bac à sable sans navigation du haut) : repli immédiat.
        window.location.href = url;
        return;
    }

    // Le refus est le plus souvent SILENCIEUX : l'affectation ci-dessus paraît
    // réussir et rien ne bouge. Si la page est toujours là, on se rabat.
    setTimeout(() => {
        try {
            window.location.href = url;
        } catch (e) {
            /* noop */
        }
    }, 800);
}

export default { isEmbedded, setHostUrl, getHostUrl, onHostUrlReady, askHostToNavigate, navigateOut };
